<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3';
import { useDebounceFn } from '@vueuse/core';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronDown,
    ChevronRight,
    Eye,
    FileText,
    FolderOpen,
    LayoutList,
    Link2,
    Search,
    Trash2,
} from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { store as libraryStore } from '@/actions/App/Http/Controllers/LibraryController';
import { index as libraryIndex } from '@/actions/App/Http/Controllers/LibraryController';
import { download as downloadLibraryFile } from '@/actions/App/Http/Controllers/LibraryController';
import { destroy as destroyLibraryFile } from '@/actions/App/Http/Controllers/LibraryController';
import { bulkDelete as bulkDeleteLibraryFiles } from '@/actions/App/Http/Controllers/LibraryController';
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import FileUploadZone from '@/pages/Files/FileUploadZone.vue';
import type { LibraryFile } from '@/types';

interface Pagination {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
}

interface Filters {
    search: string;
    sort: string;
    direction: string;
    per_page: number;
}

interface Props {
    files: LibraryFile[];
    pagination: Pagination;
    filters: Filters;
}

const props = defineProps<Props>();
const toast = useToast();

const csrfToken = () =>
    document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? '';

defineOptions({
    layout: [
        AppLayout,
        { breadcrumbs: [{ title: 'Mes fichiers', href: '/library' }] },
    ],
});

const searchInput = ref(props.filters.search || '');
const currentSort = ref(props.filters.sort || 'created_at');
const currentDirection = ref(props.filters.direction || 'desc');
const perPage = ref(`${props.filters.per_page || 20}`);
const groupByProject = ref(false);
const collapsedGroups = ref<Set<string>>(new Set());
const deletingFileId = ref<string | null>(null);
const inspectingFileId = ref<string | null>(null);
const selectedFileIds = ref<Set<string>>(new Set());
const isDeleteDialogOpen = ref(false);
const deleteMode = ref<'single' | 'bulk'>('single');
const deleteTargetIds = ref<string[]>([]);
const deleteTargetName = ref<string | null>(null);

const navigate = (params: Record<string, string | number | undefined>) => {
    router.get(
        libraryIndex.url(),
        {
            search: searchInput.value || undefined,
            sort: currentSort.value,
            direction: currentDirection.value,
            per_page: Number(perPage.value),
            page: undefined,
            ...params,
        },
        { preserveState: true, replace: true },
    );
};

const debouncedSearch = useDebounceFn(() => {
    navigate({ page: 1 });
}, 300);

watch(searchInput, () => debouncedSearch());

watch(perPage, () => navigate({ per_page: Number(perPage.value), page: 1 }));

const toggleSort = (column: string) => {
    if (currentSort.value === column) {
        currentDirection.value =
            currentDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        currentSort.value = column;
        currentDirection.value = 'asc';
    }

    navigate({});
};

const sortIcon = (column: string) => {
    if (currentSort.value !== column) {
        return ArrowUpDown;
    }

    return currentDirection.value === 'asc' ? ArrowUp : ArrowDown;
};

const formatSize = (bytes: number) => {
    if (bytes === 0) {
        return '0 B';
    }

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Grouped view
interface GroupedProject {
    id: string | null;
    name: string;
    files: LibraryFile[];
}

const groupedFiles = computed<GroupedProject[]>(() => {
    const projectMap = new Map<string, GroupedProject>();
    const noProject: LibraryFile[] = [];

    for (const file of props.files) {
        if (file.projects.length === 0) {
            noProject.push(file);
        } else {
            for (const project of file.projects) {
                if (!projectMap.has(project.id)) {
                    projectMap.set(project.id, {
                        id: project.id,
                        name: project.name,
                        files: [],
                    });
                }

                projectMap.get(project.id)!.files.push(file);
            }
        }
    }

    const groups: GroupedProject[] = [...projectMap.values()];

    if (noProject.length > 0) {
        groups.push({ id: null, name: 'Sans projet', files: noProject });
    }

    return groups;
});

const allVisibleFileIds = computed(() => props.files.map((file) => file.id));
const selectedCount = computed(() => selectedFileIds.value.size);
const areAllVisibleSelected = computed(
    () =>
        allVisibleFileIds.value.length > 0 &&
        allVisibleFileIds.value.every((id) => selectedFileIds.value.has(id)),
);
const selectAllState = computed<boolean | 'indeterminate'>(() => {
    if (selectedCount.value === 0) {
        return false;
    }

    if (areAllVisibleSelected.value) {
        return true;
    }

    return 'indeterminate';
});
const hasAnySelection = computed(() => selectedCount.value > 0);

const toggleGroup = (groupKey: string) => {
    if (collapsedGroups.value.has(groupKey)) {
        collapsedGroups.value.delete(groupKey);
    } else {
        collapsedGroups.value.add(groupKey);
    }
};

const goToPage = (page: number) => {
    navigate({ page });
};

const handleUploadsCompleted = () => {
    router.reload({ only: ['files', 'pagination', 'filters'] });
};

watch(
    () => props.files.map((file) => file.id),
    (ids) => {
        const allowedIds = new Set(ids);

        selectedFileIds.value = new Set(
            [...selectedFileIds.value].filter((id) => allowedIds.has(id)),
        );
    },
);

const updateFileSelection = (
    fileId: string,
    checked: boolean | 'indeterminate',
) => {
    const next = new Set(selectedFileIds.value);

    if (checked === true) {
        next.add(fileId);
    } else {
        next.delete(fileId);
    }

    selectedFileIds.value = next;
};

const toggleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
        selectedFileIds.value = new Set(allVisibleFileIds.value);

        return;
    }

    selectedFileIds.value = new Set();
};

const clearSelection = () => {
    selectedFileIds.value = new Set();
};

const openDeleteDialog = (options: {
    ids: string[];
    mode: 'single' | 'bulk';
    name?: string | null;
}) => {
    if (deletingFileId.value) {
        return;
    }

    deleteTargetIds.value = options.ids;
    deleteMode.value = options.mode;
    deleteTargetName.value = options.name ?? null;
    isDeleteDialogOpen.value = true;
};

const deleteFile = (file: LibraryFile) => {
    openDeleteDialog({
        ids: [file.id],
        mode: 'single',
        name: file.original_filename,
    });
};

const deleteSelectedFiles = () => {
    if (!hasAnySelection.value) {
        return;
    }

    openDeleteDialog({
        ids: [...selectedFileIds.value],
        mode: 'bulk',
    });
};

const performDeleteRequest = async (
    url: string,
    body?: Record<string, unknown>,
) => {
    const response = await fetch(url, {
        method: body ? 'POST' : 'DELETE',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
        throw new Error('delete-failed');
    }
};

const confirmDelete = async () => {
    if (deleteTargetIds.value.length === 0 || deletingFileId.value) {
        return;
    }

    deletingFileId.value =
        deleteMode.value === 'single' ? deleteTargetIds.value[0] : '__bulk__';

    try {
        if (deleteMode.value === 'bulk') {
            await performDeleteRequest(bulkDeleteLibraryFiles.url(), {
                ids: deleteTargetIds.value,
            });

            clearSelection();
            toast.success(
                `${deleteTargetIds.value.length} fichier(s) supprimé(s).`,
            );
        } else {
            await performDeleteRequest(
                destroyLibraryFile.url(deleteTargetIds.value[0]),
            );

            selectedFileIds.value.delete(deleteTargetIds.value[0]);
            toast.success('Fichier supprimé.');
        }

        router.reload({ only: ['files', 'pagination', 'filters'] });
    } catch {
        toast.error(
            deleteMode.value === 'bulk'
                ? 'Impossible de supprimer la sélection.'
                : 'Impossible de supprimer ce fichier.',
        );
    } finally {
        deletingFileId.value = null;
        isDeleteDialogOpen.value = false;
        deleteTargetIds.value = [];
        deleteTargetName.value = null;
    }
};

const inspectFile = async (file: LibraryFile) => {
    if (inspectingFileId.value) {
        return;
    }

    inspectingFileId.value = file.id;

    try {
        const response = await fetch(downloadLibraryFile.url(file.id), {
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
        });

        const payload = await response.json();

        if (!response.ok || typeof payload.url !== 'string') {
            throw new Error('inspect-failed');
        }

        window.open(payload.url, '_blank', 'noopener,noreferrer');
    } catch {
        toast.error('Impossible d’ouvrir ce fichier.');
    } finally {
        inspectingFileId.value = null;
    }
};

const goToAssociatedProject = (file: LibraryFile) => {
    const project = file.projects[0];

    if (!project) {
        return;
    }

    router.visit(`/projects/${project.id}`);
};

const deleteDialogTitle = computed(() =>
    deleteMode.value === 'bulk'
        ? 'Supprimer les fichiers sélectionnés ?'
        : 'Supprimer ce fichier ?',
);

const deleteDialogDescription = computed(() => {
    if (deleteMode.value === 'bulk') {
        return `Cette action supprimera définitivement ${deleteTargetIds.value.length} fichier(s) de votre bibliothèque.`;
    }

    return `Le fichier "${deleteTargetName.value}" sera supprimé définitivement de votre bibliothèque.`;
});
</script>

<template>
    <Head title="Mes fichiers" />

    <div class="flex flex-col gap-6 p-6">
        <!-- Page header -->
        <div
            class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
            <div>
                <h1 class="text-2xl font-bold tracking-tight">Mes fichiers</h1>
                <p class="text-muted-foreground">
                    Tous vos fichiers PDF, accessibles en un seul endroit.
                </p>
            </div>
            <Button
                variant="outline"
                size="sm"
                class="gap-2"
                @click="groupByProject = !groupByProject"
            >
                <component
                    :is="groupByProject ? LayoutList : FolderOpen"
                    class="size-4"
                />
                {{ groupByProject ? 'Vue à plat' : 'Grouper par projet' }}
            </Button>
        </div>

        <!-- Upload zone -->
        <FileUploadZone
            :upload-url="libraryStore.url()"
            @completed="handleUploadsCompleted"
        />

        <!-- Filters -->
        <div class="flex flex-wrap items-center gap-3">
            <div class="relative w-full max-w-sm">
                <Search
                    class="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground"
                />
                <Input
                    v-model="searchInput"
                    type="search"
                    placeholder="Rechercher un fichier..."
                    class="pl-9"
                />
            </div>
            <div class="flex flex-wrap items-center gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    :disabled="
                        !hasAnySelection || deletingFileId === '__bulk__'
                    "
                    @click="deleteSelectedFiles"
                >
                    <Trash2 class="mr-2 h-4 w-4" />
                    {{
                        deletingFileId === '__bulk__'
                            ? 'Suppression...'
                            : `Supprimer (${selectedCount})`
                    }}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    :disabled="!hasAnySelection"
                    @click="clearSelection"
                >
                    Réinitialiser la sélection
                </Button>
            </div>
            <div class="ml-auto flex items-center gap-2">
                <p class="text-sm text-muted-foreground">Par page</p>
                <Select v-model="perPage">
                    <SelectTrigger class="h-8 w-17.5">
                        <SelectValue :placeholder="perPage" />
                    </SelectTrigger>
                    <SelectContent side="top">
                        <SelectItem
                            v-for="size in ['10', '20', '30', '40', '50']"
                            :key="size"
                            :value="size"
                        >
                            {{ size }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>

        <!-- Table - Flat view -->
        <div
            v-if="!groupByProject"
            class="rounded-lg border border-border bg-card shadow-sm"
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead class="w-10">
                            <Checkbox
                                :model-value="selectAllState"
                                @update:model-value="toggleSelectAll"
                            />
                        </TableHead>
                        <TableHead
                            class="cursor-pointer select-none"
                            @click="toggleSort('original_filename')"
                        >
                            <div class="flex items-center gap-2">
                                Nom du fichier
                                <component
                                    :is="sortIcon('original_filename')"
                                    class="h-4 w-4 text-muted-foreground"
                                />
                            </div>
                        </TableHead>
                        <TableHead
                            class="cursor-pointer select-none"
                            @click="toggleSort('size_bytes')"
                        >
                            <div class="flex items-center gap-2">
                                Taille
                                <component
                                    :is="sortIcon('size_bytes')"
                                    class="h-4 w-4 text-muted-foreground"
                                />
                            </div>
                        </TableHead>
                        <TableHead>Projet(s)</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead
                            class="cursor-pointer select-none"
                            @click="toggleSort('created_at')"
                        >
                            <div class="flex items-center gap-2">
                                Date d'upload
                                <component
                                    :is="sortIcon('created_at')"
                                    class="h-4 w-4 text-muted-foreground"
                                />
                            </div>
                        </TableHead>
                        <TableHead class="w-32 text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow
                        v-for="file in files"
                        :key="file.id"
                        class="transition-colors"
                    >
                        <TableCell>
                            <Checkbox
                                :model-value="selectedFileIds.has(file.id)"
                                @update:model-value="
                                    (checked) =>
                                        updateFileSelection(file.id, checked)
                                "
                            />
                        </TableCell>
                        <TableCell>
                            <div class="flex items-center gap-2">
                                <FileText class="h-4 w-4 shrink-0 text-brand" />
                                <span
                                    class="max-w-64 truncate font-medium"
                                    :title="file.original_filename"
                                >
                                    {{ file.original_filename }}
                                </span>
                            </div>
                        </TableCell>
                        <TableCell class="text-muted-foreground">
                            {{ formatSize(file.size_bytes) }}
                        </TableCell>
                        <TableCell>
                            <div class="flex flex-wrap gap-1">
                                <Link
                                    v-for="project in file.projects"
                                    :key="project.id"
                                    :href="`/projects/${project.id}`"
                                >
                                    <Badge
                                        variant="secondary"
                                        class="cursor-pointer hover:bg-secondary/80"
                                    >
                                        {{ project.name }}
                                    </Badge>
                                </Link>
                                <span
                                    v-if="file.projects.length === 0"
                                    class="text-sm text-muted-foreground italic"
                                >
                                    —
                                </span>
                            </div>
                        </TableCell>
                        <TableCell>
                            <Badge
                                v-if="file.matched"
                                variant="default"
                                class="border-emerald-200 bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                            >
                                Rattaché
                            </Badge>
                            <Badge v-else variant="secondary">
                                Non rattaché
                            </Badge>
                        </TableCell>
                        <TableCell class="text-muted-foreground">
                            {{
                                new Date(file.created_at).toLocaleDateString(
                                    'fr-FR',
                                )
                            }}
                        </TableCell>
                        <TableCell class="text-right">
                            <div class="flex items-center justify-end gap-1">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    class="size-8 text-muted-foreground hover:text-foreground"
                                    :disabled="inspectingFileId === file.id"
                                    :title="
                                        inspectingFileId === file.id
                                            ? 'Ouverture…'
                                            : 'Ouvrir le PDF'
                                    "
                                    @click="inspectFile(file)"
                                >
                                    <Eye class="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    class="size-8"
                                    :class="
                                        file.matched
                                            ? 'text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800'
                                            : 'text-muted-foreground/50'
                                    "
                                    :disabled="file.projects.length === 0"
                                    :title="
                                        file.projects.length > 0
                                            ? 'Aller au projet'
                                            : 'Aucun projet rattaché'
                                    "
                                    @click="goToAssociatedProject(file)"
                                >
                                    <Link2 class="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    class="size-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                                    :disabled="
                                        deletingFileId === file.id ||
                                        deletingFileId === '__bulk__'
                                    "
                                    title="Supprimer le fichier"
                                    @click="deleteFile(file)"
                                >
                                    <Trash2 class="h-4 w-4" />
                                </Button>
                            </div>
                        </TableCell>
                    </TableRow>
                    <TableRow v-if="files.length === 0">
                        <TableCell
                            colspan="7"
                            class="h-24 text-center text-muted-foreground italic"
                        >
                            Aucun fichier trouvé.
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>

        <!-- Grouped view -->
        <div v-else class="space-y-4">
            <div
                v-for="group in groupedFiles"
                :key="group.id ?? 'no-project'"
                class="rounded-lg border border-border bg-card shadow-sm"
            >
                <button
                    class="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                    @click="toggleGroup(group.id ?? 'no-project')"
                >
                    <component
                        :is="
                            collapsedGroups.has(group.id ?? 'no-project')
                                ? ChevronRight
                                : ChevronDown
                        "
                        class="h-4 w-4 text-muted-foreground"
                    />
                    <FolderOpen class="h-4 w-4 text-brand" />
                    <span class="font-semibold">{{ group.name }}</span>
                    <Badge variant="secondary" class="ml-auto">
                        {{ group.files.length }} fichier{{
                            group.files.length > 1 ? 's' : ''
                        }}
                    </Badge>
                </button>

                <div v-show="!collapsedGroups.has(group.id ?? 'no-project')">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead class="w-10">
                                    <Checkbox
                                        :model-value="selectAllState"
                                        @update:model-value="toggleSelectAll"
                                    />
                                </TableHead>
                                <TableHead>Nom du fichier</TableHead>
                                <TableHead>Taille</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead>Date d'upload</TableHead>
                                <TableHead class="w-32 text-right"
                                    >Actions</TableHead
                                >
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow
                                v-for="file in group.files"
                                :key="file.id"
                            >
                                <TableCell>
                                    <Checkbox
                                        :model-value="
                                            selectedFileIds.has(file.id)
                                        "
                                        @update:model-value="
                                            (checked) =>
                                                updateFileSelection(
                                                    file.id,
                                                    checked,
                                                )
                                        "
                                    />
                                </TableCell>
                                <TableCell>
                                    <div class="flex items-center gap-2">
                                        <FileText
                                            class="h-4 w-4 shrink-0 text-brand"
                                        />
                                        <span
                                            class="max-w-64 truncate font-medium"
                                            :title="file.original_filename"
                                        >
                                            {{ file.original_filename }}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell class="text-muted-foreground">
                                    {{ formatSize(file.size_bytes) }}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        v-if="file.matched"
                                        variant="default"
                                        class="border-emerald-200 bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                                    >
                                        Rattaché
                                    </Badge>
                                    <Badge v-else variant="secondary">
                                        Non rattaché
                                    </Badge>
                                </TableCell>
                                <TableCell class="text-muted-foreground">
                                    {{
                                        new Date(
                                            file.created_at,
                                        ).toLocaleDateString('fr-FR')
                                    }}
                                </TableCell>
                                <TableCell class="text-right">
                                    <div
                                        class="flex items-center justify-end gap-1"
                                    >
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            class="size-8 text-muted-foreground hover:text-foreground"
                                            :disabled="
                                                inspectingFileId === file.id
                                            "
                                            :title="
                                                inspectingFileId === file.id
                                                    ? 'Ouverture…'
                                                    : 'Ouvrir le PDF'
                                            "
                                            @click="inspectFile(file)"
                                        >
                                            <Eye class="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            class="size-8"
                                            :class="
                                                file.matched
                                                    ? 'text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800'
                                                    : 'text-muted-foreground/50'
                                            "
                                            :disabled="
                                                file.projects.length === 0
                                            "
                                            :title="
                                                file.projects.length > 0
                                                    ? 'Aller au projet'
                                                    : 'Aucun projet rattaché'
                                            "
                                            @click="goToAssociatedProject(file)"
                                        >
                                            <Link2 class="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            class="size-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                                            :disabled="
                                                deletingFileId === file.id ||
                                                deletingFileId === '__bulk__'
                                            "
                                            title="Supprimer le fichier"
                                            @click="deleteFile(file)"
                                        >
                                            <Trash2 class="h-4 w-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>

            <p
                v-if="groupedFiles.length === 0"
                class="py-12 text-center text-muted-foreground italic"
            >
                Aucun fichier trouvé.
            </p>
        </div>

        <!-- Pagination -->
        <div
            v-if="pagination.last_page > 1"
            class="flex items-center justify-between px-2"
        >
            <p class="text-sm text-muted-foreground">
                {{ pagination.total }} fichier{{
                    pagination.total > 1 ? 's' : ''
                }}
                au total
            </p>
            <div class="flex items-center gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    :disabled="pagination.current_page <= 1"
                    @click="goToPage(pagination.current_page - 1)"
                >
                    Précédent
                </Button>
                <span class="text-sm font-medium">
                    Page {{ pagination.current_page }} sur
                    {{ pagination.last_page }}
                </span>
                <Button
                    variant="outline"
                    size="sm"
                    :disabled="pagination.current_page >= pagination.last_page"
                    @click="goToPage(pagination.current_page + 1)"
                >
                    Suivant
                </Button>
            </div>
        </div>

        <ConfirmDialog
            v-model:open="isDeleteDialogOpen"
            :title="deleteDialogTitle"
            :description="deleteDialogDescription"
            confirm-text="Supprimer"
            cancel-text="Annuler"
            :loading="deletingFileId !== null"
            @confirm="confirmDelete"
        />
    </div>
</template>
